import * as React from 'react'; // eslint-disable-line
import { memo } from 'react';
import styled from 'styled-components';
import classNames from 'classnames';

import { COLOR } from '../../../../packages/constants';
import { camelToKebab } from '../../../../../Scripts/Spotlight/Components/CamelToKebab';
import { AccountChildrenType, AccountType, DataGridData, ExpansionMode, isExpanded, TitleBar } from '../../data-grid';
import { AccountCell } from '../account-cell/account-cell';

import { AddFYTotals } from '../add-fy-totals/add-fy-total';
import { DataGridTotal, StyledFloatTable, FloatTableHead } from '../table/table';

const FYTotalColumnContainer = styled.div<{ disabled: boolean }>`
    ${(props) => props.disabled && `opacity: 0.2;`};
`;

const HeaderTable = styled(StyledFloatTable)`
    border-right: 0px;
    width: 100%;
`;

const FYTotalAccountCellHeading = styled(AccountCell)`
    border-left-color: ${COLOR.DATAGRID.DATAGRID_DIVIDER_LINE};
`;

const FYHeadingYearText = styled.span`
    font-weight: 400;
`;

const MemoizedAddFYTotals = memo(AddFYTotals);

interface FYTotalColumn {
    dataId: string;
    gridData: DataGridData;
    expansionMode: ExpansionMode;
    currentFinancialYear: number;
    financialYearStartMonth: number;
    isBalanceSheet: boolean;
    disabled: boolean;
}

export const FYTotalColumn: React.FC<React.PropsWithChildren<FYTotalColumn>> = ({
    dataId,
    gridData,
    expansionMode,
    currentFinancialYear,
    financialYearStartMonth,
    isBalanceSheet,
    disabled,
}) => {
    return (
        <FYTotalColumnContainer disabled={disabled} data-test-id={`${dataId}__fy-total-column`}>
            <TitleBar>&nbsp;</TitleBar>
            <FloatTableHead>
                <HeaderTable>
                    <thead>
                        <tr>
                            <th>
                                <FYTotalAccountCellHeading isTotal isHeader>
                                    <span>Total</span>{' '}
                                    <FYHeadingYearText>
                                        FY{`${currentFinancialYear + 1}`.substring(2)}
                                    </FYHeadingYearText>
                                </FYTotalAccountCellHeading>
                            </th>
                        </tr>
                    </thead>
                </HeaderTable>
            </FloatTableHead>
            <DataGridTotal>
                <tbody>
                    {gridData.accounts.map((account: AccountType, accountIndex: number) => (
                        <React.Fragment key={`fy-total--${account.id}-${accountIndex}`}>
                            <tr>
                                <td
                                    data-test-id={`${dataId}__data-accounts-table__td-${camelToKebab(
                                        account.name,
                                    )}-total_fy`}
                                >
                                    <AccountCell isTotal isNumber isGroup={!!account.children.length}>
                                        <MemoizedAddFYTotals
                                            dataRow={gridData.data[account.id]}
                                            currentFinancialYear={currentFinancialYear}
                                            financialYearStartMonth={financialYearStartMonth}
                                            isBalanceSheet={isBalanceSheet}
                                            endOfForecast={gridData.organisation.endDate}
                                        />
                                    </AccountCell>
                                </td>
                            </tr>
                            {account.children.map((accountChild: AccountChildrenType, accountChildIndex: number) => (
                                <tr key={`fy-total--${accountChild.id}-${accountChildIndex}`}>
                                    <td
                                        data-test-id={`${dataId}__data-accounts-table__td-${camelToKebab(
                                            accountChild.name,
                                        )}-${camelToKebab(account.name)}-total_fy`}
                                    >
                                        <AccountCell
                                            isTotal
                                            isNumber
                                            className={classNames(`parent-id-${account.id}`, {
                                                'h-display--none': !isExpanded(expansionMode, account),
                                            })}
                                        >
                                            <MemoizedAddFYTotals
                                                dataRow={gridData.data[accountChild.id]}
                                                currentFinancialYear={currentFinancialYear}
                                                financialYearStartMonth={financialYearStartMonth}
                                                isBalanceSheet={isBalanceSheet}
                                                endOfForecast={gridData.organisation.endDate}
                                            />
                                        </AccountCell>
                                    </td>
                                </tr>
                            ))}
                        </React.Fragment>
                    ))}
                </tbody>
            </DataGridTotal>
        </FYTotalColumnContainer>
    );
};
